import axios from 'axios';

// API Base URLs
const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

// Country code mapping
const COUNTRY_CODES = {
  '+1': { code: 'US', dialCode: 1 },
  '+44': { code: 'GB', dialCode: 44 },
  '+91': { code: 'IN', dialCode: 91 },
  '+61': { code: 'AU', dialCode: 61 },
  '+86': { code: 'CN', dialCode: 86 },
  '+31': { code: 'NL', dialCode: 31 },
  '+49': { code: 'DE', dialCode: 49 },
  '+33': { code: 'FR', dialCode: 33 },
  '+81': { code: 'JP', dialCode: 81 },
  '+82': { code: 'KR', dialCode: 82 },
};

/**
 * Fetch geolocation data based on user's IP
 * @returns {Promise<Object>} Geolocation data
 */
export const getGeoLocation = async () => {
  try {
    const response = await axios.get(GEOLOCATION_API, {
      headers: {
        'accept': 'text/plain'
      }
    });
    
    if (response.data && response.data.isSuccess) {
      return response.data;
    }
    
    return null;
  } catch (error) {
    console.error('Geolocation API error:', error);
    return null;
  }
};

/**
 * Submit lead to CRM API
 * @param {Object} formData - Form data containing name, email, phone, etc.
 * @param {Object} geoData - Geolocation data
 * @param {string} packageTitle - Optional package title
 * @param {number} packageAmount - Optional package amount
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (formData, geoData = null, packageTitle = '', packageAmount = 0) => {
  try {
    // Get country info from country code
    const countryInfo = COUNTRY_CODES[formData.country_code] || COUNTRY_CODES['+1'];
    
    // Clean phone number - ensure it's numeric only
    const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
    
    // Get current page URL
    const pageUrl = window.location.href;
    
    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: countryInfo.dialCode,
      country: countryInfo.code,
      phoneNumber: cleanPhone, // Numeric only, no country code prefix
      ipAddress: geoData?.data?.ipAddress || '0.0.0.0',
      description: formData.message || formData.subject || '',
      pageUrl: pageUrl,
      city: geoData?.data?.cityName || '',
      state: geoData?.data?.regionName || '',
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: packageTitle || '',
      packageAmount: packageAmount || 0
    };

    const response = await axios.post(LEAD_API, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return {
      success: true,
      data: response.data,
      statusCode: response.status
    };
    
  } catch (error) {
    // Handle error responses
    if (error.response) {
      return {
        success: false,
        data: error.response.data,
        statusCode: error.response.status,
        message: error.response.data?.errorMessage || error.response.data?.message || 'Submission failed'
      };
    }
    
    return {
      success: false,
      statusCode: 500,
      message: 'Network error. Please try again.'
    };
  }
};

/**
 * Handle form submission with geolocation and lead API
 * @param {Object} formData - Form data
 * @param {string} packageTitle - Optional package title
 * @param {number} packageAmount - Optional package amount
 * @returns {Promise<Object>} Result with success status and message
 */
export const handleFormSubmission = async (formData, packageTitle = '', packageAmount = 0) => {
  try {
    // Step 1: Get geolocation data
    const geoData = await getGeoLocation();
    
    // Step 2: Submit lead
    const result = await submitLead(formData, geoData, packageTitle, packageAmount);
    
    // Handle different status codes
    if (result.statusCode === 200 && result.data?.data?.customerId) {
      return {
        success: true,
        customerId: result.data.data.customerId,
        message: result.data.data.message || 'Lead created successfully',
        redirectUrl: `/application/main?customerid=${result.data.data.customerId}`
      };
    } else if (result.statusCode === 401) {
      return {
        success: false,
        message: result.data?.message || 'Unauthorized access'
      };
    } else if (result.statusCode === 400) {
      return {
        success: false,
        message: result.data?.errorMessage || result.data?.data?.message || 'Invalid data provided'
      };
    } else {
      return {
        success: false,
        message: result.message || 'Something went wrong. Please try again.'
      };
    }
    
  } catch (error) {
    console.error('Form submission error:', error);
    return {
      success: false,
      message: 'An unexpected error occurred. Please try again.'
    };
  }
};

export default {
  getGeoLocation,
  submitLead,
  handleFormSubmission
};
